import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ChevronDown, ChevronLeft, ChevronRight,
  TrendingUp, CalendarRange, BarChart3, ShoppingBag, Store, LineChart,
  Layers, ArrowUpDown, Target, HelpCircle, ShoppingCart, Users,
  Database, FileSpreadsheet, Plug
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const WHATSAPP_URL = 'https://wa.me/5588996527079';

const forecastPoints = [
  {
    icon: TrendingUp,
    title: 'Previsões Mensais e Anuais',
    description: 'Baseada na média diária acumulada, a previsão se ajusta automaticamente conforme novos dados entram. Você sabe se vai bater a meta antes do mês e/ou ano acabarem.'
  },
  {
    icon: Users,
    title: 'Segmentação por Vendedor, Produto ou Categoria',
    description: 'Não é só o total: veja previsões e comparativos individuais por vendedor da equipe, por linha de produto ou por categoria de venda.'
  },
  {
    icon: CalendarRange,
    title: 'Dias Úteis ou Dias Corridos',
    description: 'Lojas físicas vendem mais em dias úteis. E-commerces vendem todo dia. O dashboard se adapta à realidade do seu negócio.'
  }
];

const comparativeItems = [
  {
    label: 'YoY',
    title: 'Ano Atual x Ano Anterior',
    description: 'Compare o desempenho do ano atual com o anterior. Identifique crescimento real e sazonalidades.'
  },
  {
    label: 'MoM',
    title: 'Mês Atual x Mês Anterior',
    description: 'Acompanhe a evolução mês a mês. Detecte quedas ou picos antes que se tornem tendência.'
  },
  {
    label: 'YTD',
    title: 'Período Homólogo (YTD)',
    description: 'Acumulado do ano até o momento comparado com o mesmo período do ano anterior. Visão de longo prazo.'
  }
];

const integrations = [
  { icon: ShoppingCart, name: 'Shopify', desc: 'Integração direta via conector' },
  { icon: ShoppingBag, name: 'WooCommerce', desc: 'Integração direta via conector' },
  { icon: FileSpreadsheet, name: 'Google Sheets', desc: 'Preenchimento manual ou via importação' },
  { icon: Database, name: 'Sistemas Próprios', desc: 'Exportação de CSV ou conexão direta' },
  { icon: Plug, name: 'Outros ERPs', desc: 'Integração personalizada sob demanda' }
];

const audienceItems = [
  {
    icon: ShoppingCart,
    title: 'E-commerces & Lojas Virtuais',
    description: 'Shopify, WooCommerce ou plataformas próprias. Preveja faturamento e compare períodos para escalar com segurança e medir seu progresso.'
  },
  {
    icon: Store,
    title: 'Lojas Físicas',
    description: 'Monitore vendedores, metas mensais/anuais e tendências de vendas. Saiba antes se o mês ou ano ficará acima ou abaixo do esperado.'
  },
  {
    icon: BarChart3,
    title: 'Negócios com Dados Diários',
    description: 'Qualquer operação com histórico de dados diários que deseje sair do "escuro" e queira projetar o futuro com dados concretos, sem achismo.'
  }
];

const faqs = [
  {
    question: 'Preciso ter um histórico grande de dados para funcionar?',
    answer: 'Quanto mais dados históricos, mais precisas as previsões e comparativos. Porém, com poucos meses já é possível gerar previsões úteis e começar a acumular base.'
  },
  {
    question: 'Os dados podem vir de planilhas preenchidas manualmente?',
    answer: 'Sim! Se seu sistema não permite integração direta, podemos usar Google Sheets como fonte. Você preenche/atualiza os dados diariamente e o dashboard atualiza automaticamente.'
  },
  {
    question: 'Pode dar um exemplo da lógica das previsões?',
    answer: 'Supondo 250 dias úteis no ano, e nos 100 primeiros sua média diária de vendas foi de 100, com base nisso, é feita a multiplicação dessa média por 250, resultado numa estimativa de 25.000 Vendas. Essa lógica é então aplicada por mês, vendedor, categoria, etc.'
  },
  {
    question: 'Consigo ver as previsões e comparativos por vendedor individualmente?',
    answer: 'Sim. Os dashboards permitem segmentação da previsão por vendedor, produto, categoria ou qualquer dimensão relevante para o seu negócio.'
  },
  {
    question: 'Quanto tempo leva para configurar tudo?',
    answer: 'Depende da fonte de dados e complexidade. Se for algo mais simples fica pronto em poucos dias. Integrações e personalizações mais complexas podem levar até 2 semanas.'
  }
];

const clientLogos = [
  'Okbox', 'Pneus BH', 'Pneus Trio', 'Kb Tech', 'Fast Traslation'
];

const showcaseImages = [
  '/assets/Prev&Comp.jpeg',
  '/assets/Prev&CompSegm.jpeg',
  '/assets/PrevAno.jpeg',
  '/assets/PrevMes.jpeg'
];

const ComparativosPrevisosLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const nextImage = () => setCurrentImage((p) => (p + 1) % showcaseImages.length);
  const prevImage = () => setCurrentImage((p) => (p - 1 + showcaseImages.length) % showcaseImages.length);

  return (
    <>
      <Helmet>
        <title>Análise Comparativa e Previsão de Dados (Forecasting)</title>
        <meta name="description" content="Tome decisões baseadas em tendências. Dashboards de comparação histórica e previsões de resultados futuros." />
      </Helmet>
    <div className="page-mc-landing"></div>
    <div className="page-cp-landing">

      {/* ===== HERO PREVISÕES ===== */}
<section className="cp-hero" data-testid="cp-hero" style={{ overflow: 'hidden', padding: '40px 0' }}>
  <div className="rl-container"> {/* Usei rl-container para herdar a largura que você já validou */}
    <Link to="/services" className="back-link" data-testid="cp-back-link" style={{ marginBottom: '20px', display: 'inline-flex' }}>
      <ArrowLeft size={18} />
      Voltar aos Serviços
    </Link>

    {/* GRID PAI: Alinhamento centralizado e proporção de colunas */}
    <div className="bv-hero-grid" style={{ 
      display: 'grid', 
      gridTemplateColumns: '1.3fr 0.7fr', 
      gap: '60px', 
      alignItems: 'center', 
      marginTop: '40px',
      width: '100%'
    }}>
      
      {/* Coluna da Esquerda: Conteúdo */}
      <div className="cp-hero-content">
        <div className="cp-hero-badge">
          <LineChart size={14} />
          Comparativos & Previsões
        </div>
        <h1 className="cp-hero-title" style={{ marginTop: '15px', fontSize: '2.4rem', lineHeight: '1.2' }}>
          Saiba se você vai bater suas metas <span className="cp-accent">antes do mês ou ano acabarem</span>
        </h1>
        <p className="cp-hero-subtitle" style={{ marginTop: '20px', marginBottom: '30px', fontSize: '1.1rem' }}>
          Dashboards no Looker Studio com previsões baseadas em médias diárias e de comparativos
          para E-commerces, Lojas Físicas e muitos outros negócios.
        </p>
        <div className="cp-hero-cta-group">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-large" data-testid="cp-hero-cta">
            Quero Prever Meus Resultados
            <ArrowRight size={20} />
          </a>
        </div>
      </div>

      {/* Coluna da Direita: Gráfico de Previsão Corrigido */}
<div className="cp-hero-visual hide-on-mobile" aria-hidden="true" style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
  <div className="cp-forecast-graphic" style={{ 
    width: '100%', 
    maxWidth: '380px', 
    backgroundColor: '#161616', 
    padding: '24px', 
    borderRadius: '12px', 
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 15px 30px rgba(0,0,0,0.3)'
  }}>
    
    <div className="cp-forecast-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', alignItems: 'center' }}>
      <span style={{ fontSize: '11px', opacity: 0.6, letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '700' }}>Performance Mensal</span>
      <span style={{ backgroundColor: 'rgba(52, 211, 153, 0.1)', color: '#34d399', padding: '4px 12px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>EM DIA</span>
    </div>

    <div className="cp-forecast-bars" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
      
      {/* Bloco Meta */}
      <div className="cp-fg-row" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '13px', fontWeight: '500' }}>
          <span style={{ color: 'rgba(255,255,255,0.9)' }}>Meta de Vendas</span>
          <span style={{ color: '#ffffff' }}>R$ 120.000</span>
        </div>
        <div style={{ height: '20px', width: '100%', backgroundColor: '#333333', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#555555' }} />
        </div>
      </div>

      {/* Bloco Real */}
      <div className="cp-fg-row" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '13px', fontWeight: '500' }}>
          <span style={{ color: 'rgba(255,255,255,0.9)' }}>Realizado Atual</span>
          <span style={{ color: '#fbbf24' }}>R$ 82.400</span>
        </div>
        <div style={{ height: '20px', width: '100%', backgroundColor: '#333333', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '68%', height: '100%', backgroundColor: '#fbbf24' }} />
        </div>
      </div>

      {/* Bloco Previsão */}
      <div className="cp-fg-row" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '13px', fontWeight: '500' }}>
          <span style={{ color: 'rgba(255,255,255,0.9)' }}>Previsão de Fechamento</span>
          <span style={{ color: '#34d399' }}>R$ 106.200</span>
        </div>
        <div style={{ height: '20px', width: '100%', backgroundColor: '#333333', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '88%', height: '100%', backgroundColor: '#34d399' }} />
        </div>
      </div>

    </div>

    <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
      <p style={{ fontSize: '13px', opacity: 0.7, margin: 0 }}>
        Atingimento previsto: <strong style={{ color: '#34d399', fontSize: '15px' }}>88.5%</strong>
      </p>
    </div>
  </div>
</div>

    </div>
  </div>
</section>

      {/* ===== FORECASTING ===== */}
      <section className="cp-forecast" data-testid="cp-forecast">
        <div className="cp-container">
          <div className="cp-section-header">
            <h2 className="cp-section-title">Inteligência de Previsão (Forecasting)</h2>
            <p className="cp-section-subtitle">
              Entenda a lógica das previsões e como elas se adaptam ao seu negócio
            </p>
          </div>
          <div className="cp-forecast-grid">
            {forecastPoints.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="cp-forecast-card" data-testid={`cp-forecast-card-${i}`}>
                  <div className="cp-forecast-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="cp-forecast-card-title">{item.title}</h3>
                  <p className="cp-forecast-card-text">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== COMPARATIVOS ===== */}
      <section className="cp-comparatives" data-testid="cp-comparatives">
        <div className="cp-container">
          <div className="cp-section-header">
            <h2 className="cp-section-title">Análises Comparativas Visuais</h2>
            <p className="cp-section-subtitle">
              Tudo visual e fácil de entender. Compare períodos e identifique tendências em segundos.
            </p>
          </div>
          <div className="cp-comp-grid">
            {comparativeItems.map((item, i) => (
              <div key={i} className="cp-comp-card" data-testid={`cp-comp-card-${i}`}>
                <div className="cp-comp-badge">{item.label}</div>
                <h3 className="cp-comp-title">{item.title}</h3>
                <p className="cp-comp-text">{item.description}</p>
                <div className="cp-comp-visual">
                  <ArrowUpDown size={16} />
                  <span>Comparativo visual lado a lado</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FLEXIBILIDADE DE DADOS ===== */}
      <section className="cp-integrations" data-testid="cp-integrations">
        <div className="cp-container">
          <div className="cp-section-header">
            <h2 className="cp-section-title">Flexibilidade de Dados</h2>
            <p className="cp-section-subtitle">
              Integração fácil com diversas fontes. Use sua plataforma ou preencha manualmente.
            </p>
          </div>
          <div className="cp-int-grid">
            {integrations.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="cp-int-card" data-testid={`cp-int-card-${i}`}>
                  <div className="cp-int-icon">
                    <Icon size={24} />
                  </div>
                  <span className="cp-int-name">{item.name}</span>
                  <span className="cp-int-desc">{item.desc}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PARA QUEM É ===== */}
      <section className="cp-audience" data-testid="cp-audience">
        <div className="cp-container">
          <div className="cp-section-header">
            <h2 className="cp-section-title">Para Quem É Essa Solução?</h2>
          </div>
          <div className="cp-audience-grid">
            {audienceItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="cp-audience-card" data-testid={`cp-audience-card-${i}`}>
                  <div className="cp-audience-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="cp-audience-title">{item.title}</h3>
                  <p className="cp-audience-text">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PROVA SOCIAL ===== */}
      <section className="cp-social-proof" data-testid="cp-social-proof">
        <div className="cp-container">
          <div className="cp-section-header">
            <h2 className="cp-section-title">Empresas que Já Utilizam</h2>
          </div>
        </div>
        <div className="cp-logos-track-wrapper">
          <div className="cp-logos-track">
            {[...clientLogos, ...clientLogos].map((name, i) => (
              <div key={i} className="cp-logo-item" data-testid={`cp-logo-${i}`}>
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CARROSSEL ===== */}
      <section className="cp-showcase" data-testid="cp-showcase">
        <div className="cp-container">
          <div className="cp-section-header">
            <h2 className="cp-section-title">Veja os Dashboards</h2>
            <p className="cp-section-subtitle">
              Exemplos reais de dashboards de comparativos e previsões
            </p>
          </div>
          <div className="portfolio-detail-container">
            <div className="portfolio-image-carousel">
              <img
                src={showcaseImages[currentImage]}
                alt={`Dashboard exemplo ${currentImage + 1}`}
                className="portfolio-carousel-image"
              />
              {showcaseImages.length > 1 && (
                <>
                  <button className="carousel-btn carousel-btn-prev" onClick={prevImage} aria-label="Imagem anterior" data-testid="cp-carousel-prev">
                    <ChevronLeft size={24} />
                  </button>
                  <button className="carousel-btn carousel-btn-next" onClick={nextImage} aria-label="Próxima imagem" data-testid="cp-carousel-next">
                    <ChevronRight size={24} />
                  </button>
                  <div className="carousel-indicators">
                    {showcaseImages.map((_, index) => (
                      <button
                        key={index}
                        className={`carousel-indicator ${index === currentImage ? 'active' : ''}`}
                        onClick={() => setCurrentImage(index)}
                        aria-label={`Ir para imagem ${index + 1}`}
                      />
                    ))}
                  </div>
                  <div className="carousel-counter">
                    {currentImage + 1} / {showcaseImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="cp-faq" data-testid="cp-faq">
        <div className="cp-container cp-faq-container">
          <div className="cp-section-header">
            <div className="faq-icon-wrapper">
              <HelpCircle size={32} />
            </div>
            <h2 className="cp-section-title">Perguntas Frequentes</h2>
          </div>
          <div className="faq-grid-modern">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-card-modern ${openFaq === index ? 'faq-card-open' : ''}`}
              >
                <button
                  className="faq-question-modern"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  data-testid={`cp-faq-question-${index}`}
                >
                  <span className="faq-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="faq-question-text">{faq.question}</span>
                  <ChevronDown
                    size={20}
                    className={`faq-chevron ${openFaq === index ? 'faq-chevron-open' : ''}`}
                  />
                </button>
                <div className={`faq-answer-modern ${openFaq === index ? 'faq-answer-visible' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="cp-final-cta" data-testid="cp-final-cta">
        <div className="cp-container">
          <div className="cp-final-cta-content">
            <h2 className="cp-final-cta-title">Vamos Avaliar a Viabilidade para o Seu Negócio?</h2>
            <p className="cp-final-cta-text">
              Me conte sobre sua operação e seus dados. Eu avalio a viabilidade técnica e monto um plano personalizado.
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-large" data-testid="cp-final-cta-btn">
              Quero Prever Meus Resultados
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ComparativosPrevisosLanding;
